/**
 * CW-SF-R2 TKT-6 — Sales Cycle Trend (CEO role).
 *
 * Avg days from CreatedDate to CloseDate for won opps, grouped by
 * fiscal Q for the last N quarters. Compact line chart drawn as an
 * SVG polyline with point labels.
 */
import { useCallback } from 'react';
import { apiFetch } from '@/lib/api';
import { useAutoRefresh } from '@/services/widgetRegistry';
import type { WidgetProps } from '@/types';
import { SfWidgetShell } from './SfWidgetShell';
import { buildSfWidgetUrl, type SfWidgetEnvelope } from './sfWidgetUtils';

interface QuarterPoint {
  fiscal_year: number;
  fiscal_quarter: number;
  label: string;
  avg_days: number | null;
  won_count: number;
}
interface ApiResponse extends SfWidgetEnvelope {
  quarters: QuarterPoint[];
}

const REFRESH_S = 5 * 60;

export function SfSalesCycleTrendWidget({ config }: WidgetProps) {
  const fetcher = useCallback(
    () => apiFetch<ApiResponse>(buildSfWidgetUrl('/mod/cw/widgets/sf/sales-cycle-trend', config)),
    [config],
  );
  const { data, loading, error } = useAutoRefresh<ApiResponse>(fetcher, REFRESH_S);

  const quarters = (data?.quarters ?? []).filter((q) => q.avg_days !== null);

  const maxDays = quarters.length
    ? Math.max(...quarters.map((q) => q.avg_days ?? 0))
    : 0;
  const minDays = quarters.length
    ? Math.min(...quarters.map((q) => q.avg_days ?? 0))
    : 0;
  const range = Math.max(1, maxDays - minDays);

  const pointsAttr = quarters.map((q, i) => {
    const x = quarters.length > 1 ? (i / (quarters.length - 1)) * 100 : 50;
    const y = 100 - (((q.avg_days ?? 0) - minDays) / range) * 80 - 10; // 10..90
    return `${x.toFixed(1)},${y.toFixed(1)}`;
  }).join(' ');

  return (
    <div className="sf-widget sf-widget--cycle-trend">
      <SfWidgetShell
        envelope={data}
        loading={loading}
        error={error}
        hasItems={quarters.length > 0}
        emptyMessage="Not enough closed-won opps to compute a cycle trend."
      >
        {quarters.length > 0 && (
          <>
            <svg
              className="sf-widget__sparkline"
              viewBox="0 0 100 100"
              preserveAspectRatio="none"
              role="img"
              aria-label="Sales cycle trend"
            >
              <polyline points={pointsAttr} className="sf-widget__sparkline-line" />
              {quarters.map((q, i) => {
                const x = quarters.length > 1 ? (i / (quarters.length - 1)) * 100 : 50;
                const y = 100 - (((q.avg_days ?? 0) - minDays) / range) * 80 - 10;
                return (
                  <circle key={`${q.label}-${i}`} cx={x} cy={y} r="1.6" className="sf-widget__sparkline-dot" />
                );
              })}
            </svg>
            <ul className="sf-widget__trend-table">
              {quarters.map((q) => (
                <li key={q.label}>
                  <span className="sf-widget__trend-label">{q.label}</span>
                  <span className="sf-widget__trend-value">{q.avg_days}d</span>
                  <span className="sf-widget__trend-meta">{q.won_count} won</span>
                </li>
              ))}
            </ul>
          </>
        )}
      </SfWidgetShell>
    </div>
  );
}
